import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Select, EmptyState, Badge } from "./_ui";

const CATS = ["Photo", "Vidéo", "Affiche", "Logo", "Document"];
const EMPTY = { title: "", url: "", category: "Photo", caption: "" };

export default function MediasPanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });

  const load = () => api.get("/admin/media").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.url) return toast.error("Titre et URL requis");
    setBusy(true);
    try {
      await api.post("/admin/media", form);
      toast.success("Média ajouté");
      setAdding(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Lecture impossible"));
    reader.readAsDataURL(file);
  });

  const uploadOne = async (file) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`${file.name} > 5 MB ignoré`);
      return false;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      await api.post("/admin/media/upload", {
        title: file.name.replace(/\.[^.]+$/, ""),
        category: form.category,
        caption: form.caption,
        data_url: dataUrl,
      });
      return true;
    } catch (e) {
      console.warn(`Skipping ${file.name}:`, e?.response?.data?.detail || e?.message);
      return false;
    }
  };

  const onFilesChange = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setBusy(true);

    const BATCH_SIZE = 4; // parallel uploads per batch
    let done = 0;
    let ok = 0;
    let skipped = 0;
    const errors = [];
    setProgress({ done: 0, total: list.length, current: list[0].name });

    for (let i = 0; i < list.length; i += BATCH_SIZE) {
      const batch = list.slice(i, i + BATCH_SIZE);
      // Show first filename of this batch
      setProgress({ done, total: list.length, current: batch[0].name });

      const results = await Promise.all(batch.map(async (f) => {
        if (f.size > 5 * 1024 * 1024) {
          errors.push(`${f.name} (> 5 MB)`);
          return { success: false, skipped: true };
        }
        const success = await uploadOne(f);
        if (!success) errors.push(f.name);
        return { success, skipped: false };
      }));

      results.forEach((r) => {
        done++;
        if (r.success) ok++;
        if (r.skipped) skipped++;
      });
      setProgress({ done, total: list.length, current: batch[batch.length - 1].name });
    }

    setProgress({ done: list.length, total: list.length, current: "" });

    if (ok === list.length) {
      toast.success(`✓ ${ok} fichier(s) importé(s) avec succès`);
    } else if (ok > 0) {
      toast.warning(`${ok}/${list.length} importé(s) · ${errors.length} échec(s)${skipped ? ` · ${skipped} ignoré(s) (>5MB)` : ""}`);
      if (errors.length <= 5) {
        errors.forEach((name) => toast.error(`Échec : ${name}`));
      }
    } else {
      toast.error(`Aucun fichier importé · ${errors.length} échec(s)`);
    }

    setAdding(false); setForm(EMPTY); await load();
    setBusy(false);
    setTimeout(() => setProgress({ done: 0, total: 0, current: "" }), 2500);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    await api.delete(`/admin/media/${id}`);
    toast.success("Supprimé"); await load();
  };

  if (!items) return (<><PageHeader title="Médias" /><Spinner /></>);

  const filtered = filter === "all" ? items : items.filter((m) => m.category === filter);

  return (
    <div data-testid="admin-medias">
      <PageHeader
        title="Médias"
        subtitle="Bibliothèque visuelle : photos officielles, vidéos, affiches et supports de campagne."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="media-create-btn">
              <Plus size={16} /> Ajouter un média
            </PrimaryButton>
          )
        }
      />

      <div className="flex flex-wrap gap-2 mb-5">
        {["all", ...CATS].map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              filter === c
                ? "bg-[#D4AF37] text-[#0B1220] border-[#D4AF37]"
                : "bg-transparent text-slate-300 border-white/10 hover:border-[#D4AF37]/40"
            }`}
            data-testid={`media-filter-${c}`}
          >
            {c === "all" ? "Tous" : c}
          </button>
        ))}
      </div>

      {adding && (
        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="media-title" />
            <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </Select>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Option 1 — Importer un ou plusieurs fichiers (max 5 MB / fichier)</label>
              <label
                htmlFor="media-file-input"
                className={`flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-lg bg-[#0B1220] border border-dashed border-[#D4AF37]/40 text-sm text-[#D4AF37] hover:border-[#D4AF37] cursor-pointer transition-colors ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                📎 Choisir des fichiers… (sélection multiple)
              </label>
              <input
                id="media-file-input"
                type="file"
                accept="image/*,video/*"
                multiple
                disabled={busy}
                onChange={(e) => onFilesChange(e.target.files)}
                className="hidden"
                data-testid="media-file"
              />
              <div className="text-[10px] text-slate-500 mt-1.5">Astuce : maintenez <kbd className="px-1 bg-white/5 rounded">Ctrl</kbd> (ou <kbd className="px-1 bg-white/5 rounded">⌘</kbd> sur Mac) pour sélectionner plusieurs fichiers.</div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Option 2 — URL externe (1 seul)</label>
              <Input placeholder="https://…" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="media-url" />
            </div>
          </div>

          {progress.total > 0 && (
            <div className="mt-4 p-4 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30" data-testid="upload-progress">
              <div className="flex items-center justify-between text-xs text-[#D4AF37] mb-2">
                <span className="font-semibold">
                  {progress.done < progress.total ? `Import en cours… ${progress.done}/${progress.total}` : `✓ Import terminé (${progress.total} fichiers)`}
                </span>
                <span className="font-mono font-bold">{Math.round((progress.done / progress.total) * 100)}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#D4AF37] to-[#B5952F] transition-all duration-300"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
              {progress.current && progress.done < progress.total && (
                <div className="text-[10px] text-slate-400 mt-2 truncate">📄 {progress.current}</div>
              )}
              {progress.total > 20 && progress.done < progress.total && (
                <div className="text-[10px] text-amber-300 mt-2 flex items-center gap-1.5">
                  ⏳ Import par lots de 4 fichiers en parallèle. Ne fermez pas cette page.
                </div>
              )}
            </div>
          )}

          <Input placeholder="Légende (facultatif)" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="mt-3" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} disabled={busy} data-testid="media-save"><Save size={16} /> Enregistrer (URL)</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={ImageIcon} title="Bibliothèque vide" hint="Ajoutez des photos officielles A.F.B." />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((m) => (
            <Card key={m.id} className="overflow-hidden group" data-testid={`media-${m.id}`}>
              <div className="relative aspect-[4/3] bg-[#0B1220]">
                {m.category === "Vidéo" ? (
                  <video src={m.url} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={m.url} alt={m.title} className="w-full h-full object-cover" loading="lazy" />
                )}
                <button
                  onClick={() => remove(m.id)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 grid place-items-center text-white/80 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#CE1126]"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <div className="p-3">
                <Badge color="slate">{m.category}</Badge>
                <div className="text-sm text-white mt-2 truncate">{m.title}</div>
                {m.caption && <div className="text-xs text-slate-500 mt-0.5 truncate">{m.caption}</div>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
